import { artifactDownloadPageURL, artifactDownloadPath } from "./artifact-download"

export type CoworkProject = {
  id: string
  company_id: string
  owner_user_id: string
  name: string
  description?: string | null
  status: string
  default_workflow_id?: string | null
  default_period_id?: string | null
  project_metadata: Record<string, unknown>
  project_role: "viewer" | "editor" | "owner"
  created_at: string
  updated_at: string
}

export type CoworkCompany = {
  id: string
  name: string
  status: string
  role: string
}

export type CoworkCompanySummary = CoworkCompany & {
  country?: string | null
  registration_number?: string | null
  vat_number?: string | null
  projects: number
  files: number
  reports: number
  active_workflows: number
  last_activity?: string | null
}

export type CoworkCompanyAccountingProfile = {
  company_id: string
  jurisdiction?: string | null
  base_currency?: string | null
  fiscal_year_start?: string | null
  fiscal_year_end?: string | null
  vat_number?: string | null
  vat_reporting_frequency?: "monthly" | "quarterly" | "not_registered" | null
  reporting_framework?: string | null
  business_activity: Record<string, unknown>
  direct_cost_rules: string[]
  operating_expense_rules: string[]
  selling_expense_rules: string[]
  capitalization_rules: Array<Record<string, unknown> | string>
  account_mappings: Record<string, unknown>
  expense_taxonomy: Record<string, unknown>
  fixed_asset_taxonomy: Record<string, unknown>
  pnl_taxonomy: Record<string, unknown>
  materiality: Record<string, unknown>
  metadata: Record<string, unknown>
}

export type CoworkWorkspaceMember = {
  user_id: string
  email?: string | null
  name?: string | null
  username?: string | null
  role: "viewer" | "member" | "admin" | "owner"
  status: string
  company_ids: string[]
}

export type CoworkWorkspaceInvitation = {
  id: string
  email: string
  role: "viewer" | "member" | "admin"
  status: string
  invited_by?: string | null
  expires_at: string
  created_at: string
}

export type CoworkWorkspaceSettings = {
  workspace_name: string
  slug?: string
  onboarded?: boolean
  default_agent_id: string
  response_language?: string
  country?: string | null
  plan?: string
  seat_limit?: number
  seat_used?: number
  trial?: {
    companies_used: number
    companies_limit: number
    reports_used: number
    reports_limit: number
    projects_used: number
    projects_limit: number
    storage_used_bytes: number
    storage_limit_bytes: number
    chats_used: number
    chats_limit: number
    messages_used: number
    messages_limit: number
    ends_at: string | null
  } | null
}

export type CoworkAgentAssignment = {
  id: string
  scope_type: "workspace" | "entity" | "project"
  scope_id?: string | null
  scope_name?: string | null
}

export type CoworkWorkspaceAgent = {
  id: string
  workspace_company_id: string
  created_by_user_id: string
  name: string
  purpose: string
  instructions: string
  mode: "primary" | "subagent" | "all"
  status: "active" | "disabled"
  template_id?: string | null
  tools: Record<string, boolean>
  integrations: string[]
  permissions: Record<string, "allow" | "ask" | "deny">
  metadata: Record<string, unknown>
  assignments: CoworkAgentAssignment[]
  created_at: string
  updated_at: string
}

export type CoworkWorkspaceAgentInput = {
  name: string
  purpose?: string
  instructions?: string
  mode?: CoworkWorkspaceAgent["mode"]
  status?: CoworkWorkspaceAgent["status"]
  template_id?: string
  tools?: Record<string, boolean>
  integrations?: string[]
  permissions?: Record<string, "allow" | "ask" | "deny">
  metadata?: Record<string, unknown>
  assignments?: Array<Pick<CoworkAgentAssignment, "scope_type" | "scope_id">>
}

export type CoworkProjectSummary = CoworkProject & {
  company_name: string
  entity_count: number
  files: number
  reports: number
  workflows: number
  last_activity?: string | null
}

export type CoworkSession = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  automation_workflow_id?: string | null
  workflow_run_id?: string | null
  user_id: string
  title: string
  status: string
  remote_opencode_session_id?: string | null
  last_message_at?: string | null
  session_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkPortfolioChat = CoworkSession & {
  company_name: string
  project_name: string
  workflow_name?: string | null
  period_name?: string | null
  scope_type: "workspace" | "company" | "project"
  scope_name: string
  message_count: number
}

export type CoworkReportingWorkflowConfig = {
  template_id: string
  template_version: number
  report_type: string
  instructions?: string
  visible_prompt: string
  report_language: string
  file_ids: string[]
  period_id?: string | null
  account_card?: import("./report-templates").AccountCardSelection | null
}

export type CoworkReportRegistry = {
  schema_version: number
  domains: Array<{
    id: string
    version: string
    runtime_agent: string
    capabilities: string[]
    knowledge_types: string[]
    available: boolean
  }>
  templates: Array<{
    id: string
    version: number
    domain: string
    report_type: string
    jurisdiction: string | null
    availability: "enabled" | "disabled"
    validation_maturity: "contract" | "integrated"
    available: boolean
    definition: Record<string, unknown> | null
  }>
}

export type CoworkWorkflow = {
  id: string
  company_id: string
  project_id: string
  name: string
  purpose: string
  default_agent_id?: string | null
  status: string
  steps: string[]
  input_definitions: Array<Record<string, unknown> | string>
  output_definitions: Array<Record<string, unknown> | string>
  approval_required: boolean
  workflow_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkPortfolioWorkflow = CoworkWorkflow & {
  kind?: "automation"
  company_name: string
  project_name: string
  runs: number
  last_run_at?: string | null
  last_run_status?: string | null
  agent_name?: string | null
}

export type CoworkPeriodWorkflow = {
  kind: "period"
  id: string
  company_id: string
  project_id: string
  name: string
  company_name: string
  project_name: string
  status: string
  created_at: string
  updated_at: string
  runs: number
  agent_name?: string | null
}
export type CoworkWorkflowEntry = CoworkPortfolioWorkflow | CoworkPeriodWorkflow

export type CoworkWorkflowRunValidationFinding = {
  code: string
  classification: "blocking" | "incomplete" | "warning"
  status: "passed" | "failed"
  message: string
  evidence_refs?: string[]
  details?: Record<string, unknown>
}

export type CoworkWorkflowRunSourceOutcome = {
  source_id: string
  status: "completed" | "partial" | "unreadable" | "unsupported" | "failed"
  summary?: string | null
  evidence_refs?: string[]
  limitations?: Array<Record<string, unknown>>
  payload?: Record<string, unknown>
}

export type CoworkWorkflowRunOutputSnapshot = Record<string, unknown> & {
  validation_findings?: CoworkWorkflowRunValidationFinding[]
  source_outcomes?: CoworkWorkflowRunSourceOutcome[]
  review?: unknown
}

export type CoworkWorkflowRun = {
  id: string
  company_id: string
  project_id: string
  workflow_id: string
  period_id?: string | null
  session_id?: string | null
  session_remote_id?: string | null
  started_by_user_id: string
  agent_id?: string | null
  agent_name?: string | null
  status: "created" | "running" | "completed" | "needs_review" | "failed"
  validation_status: "passed" | "passed_with_warnings" | "failed" | "incomplete"
  approval_status: string
  started_at?: string | null
  completed_at?: string | null
  input_snapshot: Record<string, unknown>
  output_snapshot: CoworkWorkflowRunOutputSnapshot
  exception_count: number
  error_message?: string | null
  metadata: Record<string, unknown>
  progress_stage:
    | "starting"
    | "processing_files"
    | "analyzing"
    | "generating_report"
    | "waiting_for_input"
    | "ready"
    | "needs_review"
    | "failed"
    | "unknown"
  artifacts: CoworkWorkflowRunArtifact[]
  can_open_chat: boolean
  chat_unavailable_reason?: "missing_session" | "session_deleted" | "not_owner" | "scope_mismatch" | null
  validation_findings?: CoworkWorkflowRunValidationFinding[]
  source_outcomes?: CoworkWorkflowRunSourceOutcome[]
  file_extraction_statuses?: CoworkWorkflowRunSourceOutcome[]
  created_at: string
  updated_at: string
}

export type CoworkWorkflowPipelineData = {
  company_id: string
  project_id: string
  period_id?: string | null
  files: CoworkFile[]
  extractions: CoworkExtraction[]
  transformations: CoworkTransformation[]
  artifacts: CoworkArtifact[]
  approvals: CoworkApproval[]
}

export type CoworkWorkflowRunPipeline = CoworkWorkflowPipelineData & { workflow_id: string; run_id: string }
export type CoworkPeriodWorkflowPipeline = CoworkWorkflowPipelineData & { period_id: string }

export type CoworkWorkflowRunArtifact = {
  id: string
  title: string
  format: string
  report_type?: string | null
  report_status: "draft" | "ready_for_review" | "approved" | "blocked"
  validation_status: "passed" | "passed_with_warnings" | "failed" | "incomplete"
  size_bytes?: number | null
}

export type CoworkWorkflowInput = {
  name: string
  purpose?: string
  default_agent_id?: string | null
  steps?: string[]
  input_definitions?: Array<Record<string, unknown> | string>
  output_definitions?: Array<Record<string, unknown> | string>
  approval_required?: boolean
  metadata?: Record<string, unknown>
  reporting?: CoworkReportingWorkflowConfig
}

export type CoworkPeriod = {
  id: string
  company_id: string
  project_id: string
  name: string
  granularity: "year" | "quarter" | "month" | "day" | "custom"
  start_date?: string | null
  end_date?: string | null
  fiscal_year?: number | null
  period_status: "open" | "processing" | "needs_review" | "closing" | "closed" | "locked"
  is_current: boolean
  is_locked: boolean
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkMessage = {
  id: string
  project_id: string
  session_id: string
  role: "user" | "assistant" | "system" | "tool"
  content: string
  structured_payload: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkActivityLog = {
  id: string
  kind: "audit" | "message"
  event_type: string
  title: string
  description?: string | null
  project_id?: string | null
  project_name?: string | null
  workflow_id?: string | null
  workflow_name?: string | null
  session_id?: string | null
  session_title?: string | null
  user_id?: string | null
  user_email?: string | null
  user_name?: string | null
  payload: Record<string, unknown>
  details: Record<string, string>
  created_at: string
}

export type CoworkUsageStats = {
  projects: number
  workflows: number
  chats: number
  sessions: number
  agents: number
  files: number
  extractions: number
  transformations: number
  output_tables: number
  reports: number
  approvals: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
  estimated_cost: number
  last_updated_at: string
  by_project: Array<Record<string, unknown>>
  by_workflow: Array<Record<string, unknown>>
  recent_runs: Array<Record<string, unknown>>
  recent_activity: Array<Record<string, unknown>>
  file_stats: Record<string, unknown>
  efficiency: Record<string, unknown>
  data_sources: Array<Record<string, unknown>>
}

export type CoworkAccountingSummary = {
  project_id: string
  accounting_enabled: boolean
  accounting_agent_enabled: boolean
  summary: Record<string, number>
  tabs: string[]
}

export type CoworkAccountingReadiness = {
  workflow_id: string
  ready_to_close: boolean
  checks: Array<{ key: string; passed: boolean; current: number; total: number }>
  warnings: Array<{ key: string; count: number }>
  next_actions: Array<{ key: string; tab: string }>
  summary: Record<string, number>
}

export type CoworkAccountingContextUpdate = {
  selected_tab?: string
  selected_review_id?: string
  selected_document_id?: string
  selected_posting_group_id?: string
  selected_payment_match_id?: string
  source_filename?: string
  filters?: Record<string, unknown>
}

export type CoworkAccountingDocument = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  accounting_period_id?: string | null
  source_file_id?: string | null
  source_filename?: string | null
  document_type?: string | null
  document_number?: string | null
  document_date?: string | null
  due_date?: string | null
  currency?: string | null
  payment_method?: string | null
  counterparty_id?: string | null
  counterparty_name?: string | null
  counterparty_country?: string | null
  counterparty_vat_number?: string | null
  net_amount?: number | null
  vat_amount?: number | null
  rounding_amount?: number | null
  gross_amount?: number | null
  vat_rate?: number | null
  extraction_confidence?: number | null
  classification_confidence?: number | null
  vat_confidence?: number | null
  payment_match_confidence?: number | null
  processing_status: string
  review_status: string
  duplicate_status: string
  document_metadata: Record<string, unknown>
  posting_group_id?: string | null
  posting_status?: string | null
  posting_validation_status?: string | null
  posting_confidence?: number | null
  created_at: string
  updated_at: string
}

export type CoworkSuggestedPostingLine = {
  id: string
  posting_group_id: string
  debit_account?: string | null
  credit_account?: string | null
  amount: number
  currency?: string | null
  description?: string | null
  analytics_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkSuggestedPostingGroup = {
  id: string
  company_id: string
  document_id: string
  status: string
  confidence?: number | null
  validation_status: string
  validation_errors: string[]
  explanation?: string | null
  lines: CoworkSuggestedPostingLine[]
  created_at: string
  updated_at: string
}

export type CoworkAccountingReview = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  document_id: string
  posting_group_id?: string | null
  review_status: string
  reviewed_by_user_id?: string | null
  reviewed_at?: string | null
  changes_json: Record<string, unknown>
  comment?: string | null
  document?: CoworkAccountingDocument | null
  posting_group?: CoworkSuggestedPostingGroup | null
  created_at: string
  updated_at: string
}

export type CoworkPaymentTransaction = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  accounting_period_id?: string | null
  bank_account_id?: string | null
  external_id?: string | null
  transaction_date?: string | null
  counterparty_name?: string | null
  counterparty_iban?: string | null
  description?: string | null
  reference?: string | null
  amount: number
  currency?: string | null
  status: string
  transaction_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkPaymentMatch = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  document_id: string
  payment_id: string
  matched_amount: number
  currency?: string | null
  confidence?: number | null
  match_reasons: string[]
  status: string
  reviewed_by_user_id?: string | null
  reviewed_at?: string | null
  document?: CoworkAccountingDocument | null
  payment?: CoworkPaymentTransaction | null
  created_at: string
  updated_at: string
}

export type CoworkAccountingExportFile = {
  id: string
  company_id: string
  package_id: string
  file_type: string
  filename: string
  content_type: string
  checksum_sha256: string
  size: number
  file_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkAccountingExportFileDownload = CoworkAccountingExportFile & {
  content: string
}

export type CoworkAccountingExportPackage = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  review_id?: string | null
  package_type: string
  status: string
  idempotency_key: string
  checksum_sha256: string
  validation_status: string
  validation_errors: string[]
  manual_submission_status: string
  export_metadata: Record<string, unknown>
  files: CoworkAccountingExportFile[]
  created_at: string
  updated_at: string
}

export type CoworkLedgerSummary = {
  workflow_id: string
  workflow_name?: string | null
  journal_entries: number
  posting_lines: number
  debit_total: number
  credit_total: number
  imbalance: number
  balanced: boolean
  status_counts: Record<string, number>
}

export type CoworkLedgerJournalLine = {
  id: string
  posting_group_id: string
  document_id?: string | null
  payment_id?: string | null
  date?: string | null
  document_reference?: string | null
  source_filename?: string | null
  counterparty_name?: string | null
  description?: string | null
  debit_account?: string | null
  debit_account_name?: string | null
  credit_account?: string | null
  credit_account_name?: string | null
  amount: number
  currency?: string | null
  status: string
  analytics_json: Record<string, unknown>
  confidence?: number | null
  explanation?: string | null
  approved_by_user_id?: string | null
  approved_at?: string | null
}

export type CoworkLedgerTrialBalanceRow = {
  account: string
  account_name?: string | null
  opening_debit?: number | null
  opening_credit?: number | null
  period_debit: number
  period_credit: number
  closing_debit?: number | null
  closing_credit?: number | null
  opening_available: boolean
}

export type CoworkLedgerView = {
  summary: CoworkLedgerSummary
  journal: CoworkLedgerJournalLine[]
  trial_balance: CoworkLedgerTrialBalanceRow[]
}

export type CoworkAccountingTaxSource = {
  id: string
  title: string
  url: string
  source_type: string
  jurisdiction: string
  report_type: string
}

export type CoworkAccountingTaxReport = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  report_type: string
  jurisdiction: string
  reporting_period?: string | null
  status: string
  validation_status: string
  validation_errors: Array<Record<string, unknown> | string>
  approval_status: string
  prepared_by_user_id?: string | null
  approved_by_user_id?: string | null
  approved_at?: string | null
  source_citations_json: CoworkAccountingTaxSource[]
  report_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkFile = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  filename: string
  content_type: string
  size: number
  status: string
  parsed_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkFilePart = {
  id: string
  file_id: string
  name: string
  part_type: string
  index: number
  preview_text?: string | null
  part_metadata: Record<string, unknown>
}

export type CoworkFileDetail = CoworkFile & {
  parts: CoworkFilePart[]
}

export type CoworkPortfolioFile = CoworkFile & {
  session_id?: string | null
  company_name: string
  project_name: string
  period_name?: string | null
  source: string
  document_summary?: CoworkFileDocumentSummary
}

export type CoworkFileDocumentSummary = {
  extraction_state: "not_extracted" | "extracted" | "failed"
  document_type: string
  sellers: { id: string; name: string; vat_number?: string | null; registration_number?: string | null }[]
  document_numbers: string[]
  extraction_id?: string | null
}

export type CoworkFileCatalogFacet = { id: string; label: string; count: number; vat_number?: string | null }
export type CoworkFileCatalog = {
  items: CoworkPortfolioFile[]
  total: number
  available_file_ids: string[]
  sellers: CoworkFileCatalogFacet[]
  document_types: CoworkFileCatalogFacet[]
  accounting: boolean
}

export type CoworkKnowledge = {
  knowledge_id: string
  company_id: string
  project_id: string | null
  file_id: string
  status: string
  filename: string
  content_type: string
  size: number
  parsed_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkSessionContext = {
  company_id: string
  project_id: string
  project_name?: string | null
  workflow_id?: string | null
  workflow_name?: string | null
  period_id?: string | null
  period_name?: string | null
  period_granularity?: CoworkPeriod["granularity"] | null
  period_start_date?: string | null
  period_end_date?: string | null
  period_status?: CoworkPeriod["period_status"] | null
  period_is_locked?: boolean
  session_id: string
  remote_opencode_session_id: string
  title: string
  session_metadata: Record<string, unknown>
  accounting_profiles: Array<{
    entity_id: string
    entity_name: string
    jurisdiction?: string | null
    reporting_framework?: string | null
    business_activity?: Record<string, unknown>
    direct_cost_rules?: string[]
    operating_expense_rules?: string[]
    selling_expense_rules?: string[]
    capitalization_rules?: Array<Record<string, unknown> | string>
    account_mappings?: Record<string, unknown>
    expense_taxonomy?: Record<string, unknown>
    fixed_asset_taxonomy?: Record<string, unknown>
    pnl_taxonomy?: Record<string, unknown>
    materiality?: Record<string, unknown>
    metadata?: Record<string, unknown>
  }>
}

export type CoworkProjectMember = {
  id: string
  company_id: string
  project_id: string
  user_id: string
  user_email?: string | null
  user_name?: string | null
  role: "viewer" | "editor" | "owner"
  status: string
  invited_by_user_id?: string | null
  created_at: string
  updated_at: string
}

export type CoworkArtifact = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  session_id?: string | null
  workflow_run_id?: string | null
  artifact_type: string
  report_type?: string | null
  format: string
  title: string
  preview_html?: string | null
  artifact_metadata: Record<string, unknown>
  report_status: "draft" | "ready_for_review" | "approved" | "blocked"
  validation_status: "passed" | "passed_with_warnings" | "failed" | "incomplete"
  entity_scope: { type?: string; entity_ids?: string[]; [key: string]: unknown }
  period_start?: string | null
  period_end?: string | null
  comparison_period?: Record<string, unknown> | null
  reporting_currency?: string | null
  source_coverage: Record<string, unknown>
  summary: Record<string, unknown>
  exceptions: Array<Record<string, unknown>>
  validation_results: Array<Record<string, unknown>>
  lineage: Array<Record<string, unknown>>
  mapping_coverage?: number | null
  mapping_coverage_details: Record<string, unknown>
  status: string
  created_at: string
  updated_at: string
}

export type CoworkPortfolioReport = CoworkArtifact & {
  company_name: string
  project_name: string
  workflow_name?: string | null
  period_name?: string | null
}

export type CoworkExtraction = {
  field_view?: {
    contract_version: string
    data: Record<string, unknown>
    labels: Record<string, string>
    has_source_fields?: boolean
  } | null
  id: string
  project_id: string
  workflow_id?: string | null
  file_id?: string | null
  workflow_run_id?: string | null
  schema_name?: string | null
  prompt?: string | null
  result_json: Record<string, unknown>
  confidence?: number | null
  status: string
  created_at: string
  updated_at: string
}

export type CoworkTransformation = {
  id: string
  project_id: string
  workflow_id?: string | null
  workflow_run_id?: string | null
  instructions: string
  source_extraction_ids: string[]
  steps_json: Array<Record<string, unknown>>
  output_json: Record<string, unknown>
  status: string
  created_at: string
  updated_at: string
}

export type CoworkFileExtraction = CoworkExtraction & {
  company_id: string
  period_id?: string | null
  record_type: "workflow" | "source" | "report"
  summary?: string | null
  revision?: number | null
  schema_version?: string | null
  extractor_key?: string | null
  extractor_version?: string | null
  quality_status?: string | null
  limitations: Record<string, unknown>[]
  provenance: Record<string, unknown>
  superseded: boolean
  current_content?: boolean | null
}

export type CoworkReportShare = {
  id: string
  report_artifact_id: string
  shared_by_user_id: string
  recipient_email: string
  message?: string | null
  status: "pending" | "sent" | "failed"
  sent_at?: string | null
  created_at: string
}

export type CoworkSharedReport = {
  report_title: string
  report_format: string
  report_filename: string
  message?: string | null
  shared_by: string
  shared_at: string
}

export type CoworkApproval = {
  id: string
  company_id: string
  project_id: string
  report_artifact_id: string
  requested_by_user_id: string
  approver_email: string
  status: string
  comments?: string | null
  created_at: string
  updated_at: string
}

export type CoworkPortfolioApproval = CoworkApproval & {
  company_name: string
  project_name: string
  report_title: string
  report_format: string
  report_status: string
  period_name?: string | null
}

export type CoworkApprovalComment = {
  id: string
  kind: "request" | "comment" | "approved" | "rejected" | "legacy"
  author_name?: string | null
  author_email?: string | null
  body: string
  created_at: string
}

export type CoworkApprovalReview = {
  approval_id: string
  approver_email: string
  requested_by_user_id: string
  requested_by_email?: string | null
  requested_by_name?: string | null
  status: string
  comments?: string | null
  comment_history?: CoworkApprovalComment[]
  report_title: string
  report_format: string
  report_content_type: string
  report_filename: string
  report_content?: string | null
  created_at: string
  updated_at: string
}

export type CoworkApprovalDetail = CoworkApprovalReview & {
  company_id: string
  project_id: string
  report_artifact_id: string
  can_comment: boolean
}

export type CoworkConnectSession = {
  token: string
  connect_link: string
  expires_at: string
}

export type CoworkIntegrationConnection = {
  connection_id: string
  provider_config_key: string
  provider_key?: string | null
  provider?: string | null
  end_user?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
  created_at?: string | null
  updated_at?: string | null
}

export type CoworkCloudFile = {
  id: string
  name: string
  mime_type?: string | null
  size?: number | null
  is_folder: boolean
  drive_id?: string | null
  parent_id?: string | null
  path?: string | null
  version?: string | null
  modified_at?: string | null
  raw?: Record<string, unknown> | null
}

export type CoworkStreamEvent =
  | { type: "user_message"; message_id: string }
  | { type: "assistant_message"; message_id: string }
  | { type: "token"; content: string }
  | { type: "run_progress"; steps?: Array<{ label: string; status: string }> }
  | { type: "tool_start"; tool: string; message: string }
  | { type: "final_answer"; content: string }
  | { type: "error"; error: string }

let tokenProvider: ((forceRefresh?: boolean) => Promise<string>) | undefined

export class CoworkApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = "CoworkApiError"
  }
}

export function setCoworkAuthTokenProvider(provider: (forceRefresh?: boolean) => Promise<string>) {
  tokenProvider = provider
}

const jsonHeaders = async (companyID?: string) => ({
  ...(await authHeaders(false, companyID)),
  "Content-Type": "application/json",
})

export function coworkApiBaseUrl() {
  return (import.meta.env.VITE_AGENT_API_URL || "http://localhost:8088").replace(/\/$/, "")
}

export async function coworkAuthHeaders(companyID?: string) {
  return authHeaders(false, companyID)
}

export function coworkAgentMode() {
  return import.meta.env.VITE_AGENT_MODE === "true"
}

export function coworkVersion() {
  return "1.0.0"
}

export type CoworkRuntimeConnection = {
  url: string
  username: string
  password: string
  displayName?: string
}

export async function getRuntimeConnection(companyID?: string) {
  return requestJson<CoworkRuntimeConnection>("/api/runtime/connection", undefined, companyID)
}

export async function listProjects(companyID?: string) {
  return boundedRead<CoworkProject[]>("/api/projects", companyID)
}

export async function getProject(projectID: string, companyID: string) {
  return boundedRead<CoworkProject>(`/api/projects/${encodeURIComponent(projectID)}`, companyID)
}

export async function listCompanies() {
  return requestJson<CoworkCompany[]>("/api/companies")
}

export async function listCompanySummaries() {
  return requestJson<CoworkCompanySummary[]>("/api/companies/summary")
}

export type CoworkCompanyRegistrySuggestion = {
  name: string
  registration_number: string
  country: "EE"
  address?: string | null
  source_url: string
}

export type CoworkCompanyRegistryDetails = Omit<CoworkCompanyRegistrySuggestion, "address"> & {
  vat_number: string | null
}

export async function searchCompanyRegistry(query: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ q: query.trim() })
  return requestJson<CoworkCompanyRegistrySuggestion[]>(`/api/company-registry/search?${params}`, { signal })
}

export async function getCompanyRegistryDetails(registrationNumber: string, signal?: AbortSignal) {
  return requestJson<CoworkCompanyRegistryDetails>(`/api/company-registry/${encodeURIComponent(registrationNumber)}`, {
    signal,
  })
}

export async function createCompany(input: {
  name: string
  country?: string
  registration_number?: string
  vat_number?: string | null
}) {
  return requestJson<CoworkCompanySummary>("/api/companies", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function updateCompany(
  companyID: string,
  input: { name?: string; country?: string; registration_number?: string; vat_number?: string | null; status?: string },
) {
  return requestJson<CoworkCompanySummary>(`/api/companies/${companyID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export type CoworkCompanyEmployee = {
  id: string
  company_id: string
  first_name: string
  last_name: string
  personal_id: string
  created_at: string
  updated_at: string
}

export type CoworkCompanyEmployeeInput = Pick<CoworkCompanyEmployee, "first_name" | "last_name" | "personal_id">

export async function listCompanyEmployees(companyID: string, signal?: AbortSignal) {
  return requestJson<CoworkCompanyEmployee[]>(
    `/api/companies/${encodeURIComponent(companyID)}/employees`,
    { signal, cache: "no-store" },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function createCompanyEmployee(companyID: string, input: CoworkCompanyEmployeeInput) {
  return requestJson<CoworkCompanyEmployee>(
    `/api/companies/${encodeURIComponent(companyID)}/employees`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function updateCompanyEmployee(companyID: string, employeeID: string, input: CoworkCompanyEmployeeInput) {
  return requestJson<CoworkCompanyEmployee>(
    `/api/companies/${encodeURIComponent(companyID)}/employees/${encodeURIComponent(employeeID)}`,
    { method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function deleteCompanyEmployee(companyID: string, employeeID: string) {
  return requestJson<void>(
    `/api/companies/${encodeURIComponent(companyID)}/employees/${encodeURIComponent(employeeID)}`,
    { method: "DELETE" },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function getCompanyAccountingProfile(companyID: string) {
  return requestJson<CoworkCompanyAccountingProfile>(
    `/api/companies/${encodeURIComponent(companyID)}/accounting-profile`,
    undefined,
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export type CoworkCompanyAccountingProfileInput = Pick<
  CoworkCompanyAccountingProfile,
  "jurisdiction" | "base_currency" | "fiscal_year_start" | "fiscal_year_end" | "vat_number" | "vat_reporting_frequency"
>

export async function updateCompanyAccountingProfile(companyID: string, input: CoworkCompanyAccountingProfileInput) {
  return requestJson<CoworkCompanyAccountingProfile>(
    `/api/companies/${encodeURIComponent(companyID)}/accounting-profile`,
    { method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function deleteCompany(companyID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/companies/${companyID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listWorkspaceMembers() {
  return requestJson<CoworkWorkspaceMember[]>("/api/workspace/members")
}

export async function listWorkspaceInvitations() {
  return requestJson<CoworkWorkspaceInvitation[]>("/api/workspace/invitations")
}

export async function createWorkspaceInvitation(email: string, role: CoworkWorkspaceInvitation["role"]) {
  return requestJson<CoworkWorkspaceInvitation>("/api/workspace/invitations", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ email, role }),
  })
}

export async function resendWorkspaceInvitation(invitationID: string) {
  return requestJson<CoworkWorkspaceInvitation>(
    `/api/workspace/invitations/${encodeURIComponent(invitationID)}/resend`,
    {
      method: "POST",
      headers: await authHeaders(),
    },
  )
}

export async function revokeWorkspaceInvitation(invitationID: string) {
  return requestJson<void>(`/api/workspace/invitations/${encodeURIComponent(invitationID)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
}

export async function removeWorkspaceMember(userID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/workspace/members/${encodeURIComponent(userID)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export type CoworkMe = {
  user_id: string
  email?: string | null
  company_id: string
  company_role: string
  platform_admin: boolean
}

export type CoworkAccessRequest = {
  id: string
  email: string
  locale: string
  firm_name?: string | null
  status: "requested" | "approved" | "declined"
  company_id?: string | null
  created_at: string
  approved_at?: string | null
}

export async function getMe() {
  return requestJson<CoworkMe>("/api/me")
}

export async function getBootstrapMe() {
  if (localDevAuthEnabled()) return getMe()
  const token = tokenProvider ? await tokenProvider(false) : ""
  return requestJson<CoworkMe>("/api/me", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
}

export async function listAccessRequests(status: "requested" | "all" = "requested") {
  return requestJson<CoworkAccessRequest[]>(`/api/admin/access-requests?status=${status}`, undefined, undefined, {
    preserveAuthOnForbidden: true,
  })
}

export async function approveAccessRequest(requestID: string, workspaceName?: string) {
  return requestJson<CoworkAccessRequest>(
    `/api/admin/access-requests/${encodeURIComponent(requestID)}/approve`,
    { method: "POST", headers: await jsonHeaders(), body: JSON.stringify({ workspace_name: workspaceName || null }) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function declineAccessRequest(requestID: string) {
  return requestJson<CoworkAccessRequest>(
    `/api/admin/access-requests/${encodeURIComponent(requestID)}/decline`,
    { method: "POST", headers: await authHeaders() },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export type CoworkAdminUser = {
  id: string
  email: string | null
  full_name: string | null
  status: "active" | "disabled" | "deleted"
  has_password: boolean
  workspaces: string[]
  created_at: string
}

export async function listAdminUsers(search = "", offset = 0) {
  const query = new URLSearchParams({ search, offset: String(offset) })
  return requestJson<CoworkAdminUser[]>(`/api/admin/users?${query}`, undefined, undefined, {
    preserveAuthOnForbidden: true,
  })
}

export async function createAdminUser(input: { email: string; full_name: string; workspace_name: string }) {
  return requestJson<CoworkAdminUser>(
    "/api/admin/users",
    { method: "POST", headers: await jsonHeaders(), body: JSON.stringify(input) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function updateAdminUser(userID: string, input: { full_name?: string; status?: "active" | "disabled" }) {
  return requestJson<CoworkAdminUser>(
    `/api/admin/users/${encodeURIComponent(userID)}`,
    { method: "PATCH", headers: await jsonHeaders(), body: JSON.stringify(input) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function deleteAdminUser(userID: string) {
  return requestJson<void>(
    `/api/admin/users/${encodeURIComponent(userID)}`,
    { method: "DELETE", headers: await authHeaders() },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export type CoworkWorkspace = {
  id: string
  name: string
  slug: string
  role: "viewer" | "member" | "admin" | "owner"
  onboarded: boolean
  company_ids: string[]
}

export type CoworkHandleAvailability = {
  value: string
  available: boolean
  reason?: "invalid" | "reserved" | "taken" | null
  suggestion?: string | null
}

export type CoworkWorkspaceProfile = {
  user_id: string
  email?: string | null
  full_name?: string | null
  username: string
}

export async function listWorkspaces() {
  return requestJson<CoworkWorkspace[]>("/api/workspaces")
}

export async function checkWorkspaceSlug(slug: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ slug })
  return requestJson<CoworkHandleAvailability>(`/api/workspaces/slug-availability?${params}`, { signal })
}

export async function checkUsername(username: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ username })
  return requestJson<CoworkHandleAvailability>(`/api/workspace/username-availability?${params}`, { signal })
}

export async function getWorkspaceProfile() {
  return requestJson<CoworkWorkspaceProfile>("/api/workspace/profile")
}

export async function updateWorkspaceProfile(input: { full_name?: string; username?: string }) {
  return requestJson<CoworkWorkspaceProfile>("/api/workspace/profile", {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export type CoworkWorkspacePlan = "internal" | "early_access" | "trial" | "platform" | "enterprise"

export type CoworkAdminWorkspace = {
  id: string
  name: string
  slug: string
  plan: CoworkWorkspacePlan
  seat_limit: number
  seats_used: number
  companies: number
  owner_email?: string | null
  billing_email?: string | null
  created_at: string
  month: string
  active_companies: number
  report_runs: number
  estimated_invoice_eur: number
  fair_use_companies: string[]
}

export async function listAdminWorkspaces(month?: string) {
  const query = month ? `?${new URLSearchParams({ month })}` : ""
  return requestJson<CoworkAdminWorkspace[]>(`/api/admin/workspaces${query}`, undefined, undefined, {
    preserveAuthOnForbidden: true,
  })
}

export async function updateAdminWorkspace(
  workspaceID: string,
  input: { plan?: CoworkWorkspacePlan; seat_limit?: number },
) {
  return requestJson<CoworkAdminWorkspace>(
    `/api/admin/workspaces/${encodeURIComponent(workspaceID)}`,
    { method: "PATCH", headers: await jsonHeaders(), body: JSON.stringify(input) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function completeOnboarding() {
  return requestJson<CoworkWorkspaceSettings>("/api/workspace/onboarding/complete", {
    method: "POST",
    headers: await authHeaders(),
  })
}

export async function saveOnboardingPreferences(input: { country: string; response_language: string }) {
  return requestJson<{ company_id: string; project_id: string; country: string; response_language: string }>(
    "/api/workspace/onboarding/preferences",
    { method: "POST", headers: await jsonHeaders(), body: JSON.stringify(input) },
  )
}

export async function getWorkspaceSettings() {
  return getWorkspaceSettingsForCompany()
}

export async function getWorkspaceSettingsForCompany(companyID?: string) {
  return requestJson<CoworkWorkspaceSettings>("/api/workspace/settings", undefined, companyID)
}

export async function updateWorkspaceSettings(input: Partial<CoworkWorkspaceSettings>) {
  return requestJson<CoworkWorkspaceSettings>("/api/workspace/settings", {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function listWorkspaceAgents() {
  return listWorkspaceAgentsForCompany()
}

export async function listWorkspaceAgentsForCompany(companyID?: string) {
  return requestJson<CoworkWorkspaceAgent[]>("/api/workspace/agents", undefined, companyID)
}

export async function createWorkspaceAgent(input: CoworkWorkspaceAgentInput) {
  return requestJson<CoworkWorkspaceAgent>("/api/workspace/agents", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function updateWorkspaceAgent(agentID: string, input: Partial<CoworkWorkspaceAgentInput>) {
  return requestJson<CoworkWorkspaceAgent>(`/api/workspace/agents/${agentID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function updateWorkspaceAgentAssignments(
  agentID: string,
  assignments: Array<Pick<CoworkAgentAssignment, "scope_type" | "scope_id">>,
) {
  return requestJson<CoworkWorkspaceAgent>(`/api/workspace/agents/${agentID}/assignments`, {
    method: "PUT",
    headers: await jsonHeaders(),
    body: JSON.stringify(assignments),
  })
}

export async function updateWorkspaceAgentFocus(agentID: string, preferredSkills: string[], instructions: string) {
  return requestJson<CoworkWorkspaceAgent>(`/api/workspace/agents/${agentID}/focus`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify({ preferred_skills: preferredSkills, instructions }),
  })
}

export async function deleteWorkspaceAgent(agentID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/workspace/agents/${agentID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listProjectSummaries() {
  return boundedRead<CoworkProjectSummary[]>("/api/projects/summary")
}

export async function listActivityLogs(options?: {
  kind?: string
  search?: string
  limit?: number
  projectID?: string
  periodID?: string
  beforeID?: string
  companyID?: string
  entityID?: string
}) {
  const params = new URLSearchParams()
  if (options?.kind) params.set("kind", options.kind)
  if (options?.search) params.set("search", options.search)
  if (options?.limit) params.set("limit", String(options.limit))
  if (options?.projectID) params.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") params.set("period_id", options.periodID)
  if (options?.beforeID) params.set("before_id", options.beforeID)
  if (options?.entityID) params.set("entity_id", options.entityID)
  return requestJson<CoworkActivityLog[]>(
    `/api/activity${params.size ? `?${params.toString()}` : ""}`,
    undefined,
    options?.companyID,
  )
}

export async function getUsageStats() {
  return requestJson<CoworkUsageStats>("/api/usage/stats")
}

export async function getAccountingProjectSummary(projectID: string, workflowID?: string | null) {
  const params = workflowID ? `?workflow_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkAccountingSummary>(`/api/v1/accounting/projects/${projectID}${params}`)
}

export async function getAccountingReadiness(workflowID: string) {
  return requestJson<CoworkAccountingReadiness>(`/api/v1/accounting/workflows/${workflowID}/readiness`).catch(
    () => undefined,
  )
}

export async function listAccountingDocuments(workflowID: string) {
  return requestJson<CoworkAccountingDocument[]>(`/api/v1/accounting/workflows/${workflowID}/documents`).catch(() => [])
}

export async function createAccountingDocument(workflowID: string, sourceFileID: string) {
  return requestJson<CoworkAccountingDocument>(`/api/v1/accounting/workflows/${workflowID}/documents`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ source_file_id: sourceFileID }),
  })
}

export async function processAccountingDocument(documentID: string) {
  return requestJson<CoworkAccountingDocument>(`/api/v1/accounting/documents/${documentID}/process`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function generateAccountingPostingSuggestion(documentID: string) {
  return requestJson<CoworkSuggestedPostingGroup>(`/api/v1/accounting/documents/${documentID}/posting-suggestions`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function listAccountingReviews(workflowID: string) {
  return requestJson<CoworkAccountingReview[]>(`/api/v1/accounting/workflows/${workflowID}/reviews`).catch(() => [])
}

export async function approveAccountingReview(reviewID: string, comment?: string) {
  return requestJson<CoworkAccountingReview>(`/api/v1/accounting/reviews/${reviewID}/approve`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function rejectAccountingReview(reviewID: string, comment?: string) {
  return requestJson<CoworkAccountingReview>(`/api/v1/accounting/reviews/${reviewID}/reject`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function listPaymentTransactions(workflowID: string) {
  return requestJson<CoworkPaymentTransaction[]>(`/api/v1/accounting/workflows/${workflowID}/payments`).catch(() => [])
}

export async function importPaymentTransactions(
  workflowID: string,
  transactions: Array<Partial<CoworkPaymentTransaction> & { amount: number }>,
) {
  return requestJson<CoworkPaymentTransaction[]>(`/api/v1/accounting/workflows/${workflowID}/payments/import`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ transactions }),
  })
}

export async function listPaymentMatches(workflowID: string) {
  return requestJson<CoworkPaymentMatch[]>(`/api/v1/accounting/workflows/${workflowID}/payment-matches`).catch(() => [])
}

export async function generatePaymentMatches(workflowID: string) {
  return requestJson<CoworkPaymentMatch[]>(`/api/v1/accounting/workflows/${workflowID}/payment-matches`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function approvePaymentMatch(matchID: string) {
  return requestJson<CoworkPaymentMatch>(`/api/v1/accounting/payment-matches/${matchID}/approve`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({}),
  })
}

export async function rejectPaymentMatch(matchID: string) {
  return requestJson<CoworkPaymentMatch>(`/api/v1/accounting/payment-matches/${matchID}/reject`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({}),
  })
}

export async function listAccountingExportPackages(workflowID: string) {
  return requestJson<CoworkAccountingExportPackage[]>(
    `/api/v1/accounting/workflows/${workflowID}/export-packages`,
  ).catch(() => [])
}

export async function getAccountingLedgerView(workflowID: string) {
  return requestJson<CoworkLedgerView>(`/api/v1/accounting/workflows/${workflowID}/ledger`)
}

export async function createAccountingExportPackage(workflowID: string) {
  return requestJson<CoworkAccountingExportPackage>(`/api/v1/accounting/workflows/${workflowID}/export-packages`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ package_type: "ledger_export", formats: ["csv", "xlsx", "xml", "json", "pdf"] }),
  })
}

export async function updateAccountingExportManualStatus(packageID: string, manualSubmissionStatus: string) {
  return requestJson<CoworkAccountingExportPackage>(
    `/api/v1/accounting/export-packages/${packageID}/manual-submission`,
    {
      method: "PATCH",
      headers: await jsonHeaders(),
      body: JSON.stringify({ manual_submission_status: manualSubmissionStatus }),
    },
  )
}

export async function downloadAccountingExportFile(fileID: string) {
  return requestJson<CoworkAccountingExportFileDownload>(`/api/v1/accounting/export-files/${fileID}`)
}

export async function deleteAccountingExportFile(fileID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/v1/accounting/export-files/${fileID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) await handleAuthFailure(response)
}

export async function listAccountingTaxReports(workflowID: string) {
  return requestJson<CoworkAccountingTaxReport[]>(`/api/v1/accounting/workflows/${workflowID}/tax-reports`).catch(
    () => [],
  )
}

export async function createAccountingTaxReport(
  workflowID: string,
  body: { report_type?: string; jurisdiction?: string; reporting_period?: string | null } = {},
) {
  return requestJson<CoworkAccountingTaxReport>(`/api/v1/accounting/workflows/${workflowID}/tax-reports`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({
      report_type: body.report_type ?? "kmd",
      jurisdiction: body.jurisdiction ?? "EE",
      reporting_period: body.reporting_period ?? null,
    }),
  })
}

export async function approveAccountingTaxReport(reportID: string, comment?: string) {
  return requestJson<CoworkAccountingTaxReport>(`/api/v1/accounting/tax-reports/${reportID}/approve`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function rejectAccountingTaxReport(reportID: string, comment?: string) {
  return requestJson<CoworkAccountingTaxReport>(`/api/v1/accounting/tax-reports/${reportID}/reject`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function deleteAccountingTaxReport(reportID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/v1/accounting/tax-reports/${reportID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) await handleAuthFailure(response)
}

export async function listAccountingTaxSources(reportType = "kmd", jurisdiction = "EE") {
  const params = new URLSearchParams({ report_type: reportType, jurisdiction })
  return requestJson<CoworkAccountingTaxSource[]>(`/api/v1/accounting/tax-sources?${params.toString()}`).catch(() => [])
}

export async function createProject(name: string, companyID?: string) {
  return requestJson<CoworkProject>("/api/projects", {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({ name }),
  })
}

export async function updateProject(projectID: string, name: string, companyID?: string) {
  return requestJson<CoworkProject>(`/api/projects/${projectID}`, {
    method: "PATCH",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({ name }),
  })
}

export async function deleteProject(projectID: string, companyID?: string) {
  const headers = await authHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}`, {
    method: "DELETE",
    headers,
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listSessions(projectID: string, workflowID?: string) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkSession[]>(`/api/projects/${projectID}/sessions${query}`)
}

export async function listPortfolioChats(options?: { entityID?: string; projectID?: string }) {
  const query = new URLSearchParams()
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  return requestJson<CoworkPortfolioChat[]>(`/api/chats?${query.toString()}`)
}

export async function listWorkflows(projectID: string) {
  return requestJson<CoworkWorkflow[]>(`/api/projects/${projectID}/workflows`)
}

export async function listWorkflowEntries(options?: { entityID?: string; projectID?: string }) {
  const query = new URLSearchParams({ include_periods: "true" })
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  return requestJson<CoworkWorkflowEntry[]>(`/api/workflows?${query.toString()}`)
}

export async function listPortfolioWorkflows(options?: { entityID?: string; projectID?: string }) {
  const query = new URLSearchParams()
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  return requestJson<CoworkPortfolioWorkflow[]>(`/api/workflows?${query.toString()}`)
}

export async function getReportRegistry() {
  return requestJson<CoworkReportRegistry>("/api/report-templates")
}

export async function listPeriods(projectID: string, companyID?: string) {
  return boundedRead<CoworkPeriod[]>(`/api/projects/${projectID}/periods`, companyID)
}

export async function createPeriod(
  projectID: string,
  input: {
    name: string
    granularity: CoworkPeriod["granularity"]
    start_date?: string
    end_date?: string
    fiscal_year?: number
    is_current?: boolean
  },
  companyID?: string,
) {
  return requestJson<CoworkPeriod>(`/api/projects/${projectID}/periods`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function updatePeriod(
  projectID: string,
  periodID: string,
  input: Partial<
    Pick<
      CoworkPeriod,
      "name" | "granularity" | "start_date" | "end_date" | "fiscal_year" | "period_status" | "is_current" | "is_locked"
    >
  >,
) {
  return requestJson<CoworkPeriod>(`/api/projects/${projectID}/periods/${periodID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function closePeriod(projectID: string, periodID: string, companyID?: string) {
  return requestJson<CoworkPeriod>(
    `/api/projects/${projectID}/periods/${periodID}/close`,
    { method: "POST" },
    companyID,
  )
}

export async function reopenPeriod(projectID: string, periodID: string, companyID?: string) {
  return requestJson<CoworkPeriod>(
    `/api/projects/${projectID}/periods/${periodID}/reopen`,
    { method: "POST" },
    companyID,
  )
}

export async function createWorkflow(projectID: string, input: CoworkWorkflowInput, companyID?: string) {
  return requestJson<CoworkWorkflow>(`/api/projects/${projectID}/workflows`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function updateWorkflow(
  projectID: string,
  workflowID: string,
  input: Partial<CoworkWorkflowInput> & { status?: "active" | "disabled" },
  companyID?: string,
) {
  return requestJson<CoworkWorkflow>(`/api/projects/${projectID}/workflows/${workflowID}`, {
    method: "PATCH",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function listWorkflowRuns(projectID: string, workflowID: string, companyID?: string) {
  return requestJson<CoworkWorkflowRun[]>(`/api/projects/${projectID}/workflows/${workflowID}/runs`, {
    headers: await authHeaders(false, companyID),
  })
}

export async function runWorkflow(
  projectID: string,
  workflowID: string,
  periodID?: string,
  options?: { requestID?: string; rerunOfRunID?: string; companyID?: string; applicationLocale?: string },
) {
  return requestJson<CoworkWorkflowRun>(`/api/projects/${projectID}/workflows/${workflowID}/runs`, {
    method: "POST",
    headers: await jsonHeaders(options?.companyID),
    body: JSON.stringify({
      period_id: periodID,
      request_id: options?.requestID,
      rerun_of_run_id: options?.rerunOfRunID,
      application_locale: options?.applicationLocale,
    }),
  })
}

export async function getPeriodWorkflowPipeline(projectID: string, periodID: string, companyID: string) {
  return requestJson<CoworkPeriodWorkflowPipeline>(
    `/api/projects/${encodeURIComponent(projectID)}/periods/${encodeURIComponent(periodID)}/pipeline`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getWorkflowRunPipeline(projectID: string, workflowID: string, runID: string, companyID: string) {
  return requestJson<CoworkWorkflowRunPipeline>(
    `/api/projects/${encodeURIComponent(projectID)}/workflows/${encodeURIComponent(workflowID)}/runs/${encodeURIComponent(runID)}/pipeline`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getWorkflowRun(projectID: string, workflowID: string, runID: string, companyID?: string) {
  return requestJson<CoworkWorkflowRun>(`/api/projects/${projectID}/workflows/${workflowID}/runs/${runID}`, {
    headers: await authHeaders(false, companyID),
  })
}

export async function downloadWorkflowRunArtifact(
  projectID: string,
  workflowID: string,
  runID: string,
  artifactID: string,
  companyID?: string,
) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/projects/${projectID}/workflows/${workflowID}/runs/${runID}/artifacts/${artifactID}/download`,
    { headers: await authHeaders(false, companyID) },
  )
  if (!response.ok) await handleAuthFailure(response)
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function deleteWorkflow(projectID: string, workflowID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/workflows/${workflowID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function createSession(
  projectID: string,
  title: string,
  periodID?: string,
  metadata: Record<string, string | number | boolean> = {},
  agentID?: string,
  scope?: { type: "workspace" | "company" | "project"; entityID?: string },
  companyID?: string,
  creationRequestID?: string,
) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({
      title,
      period_id: periodID,
      agent_id: agentID,
      scope_type: scope?.type || "project",
      scope_entity_id: scope?.entityID,
      metadata,
      creation_request_id: creationRequestID,
    }),
  })
}

export async function updateSessionAccountingContext(
  projectID: string,
  sessionID: string,
  context: CoworkAccountingContextUpdate,
) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions/${sessionID}/accounting-context`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(context),
  })
}

export async function syncCoworkSessionAgent(remoteSessionID: string, runtimeAgent: string) {
  const context = await requestJson<CoworkSessionContext>(`/api/sessions/remote/${remoteSessionID}/context`)
  const capability = context.session_metadata?.report_capability as { runtime_agent?: string } | undefined
  const agent = context.session_metadata?.agent as { runtime_agent_name?: string } | undefined
  if ((capability?.runtime_agent || agent?.runtime_agent_name) === runtimeAgent) return
  await requestJson<CoworkSession>(`/api/projects/${context.project_id}/sessions/${context.session_id}/agent`, {
    method: "PATCH",
    headers: await jsonHeaders(context.company_id),
    body: JSON.stringify({ runtime_agent: runtimeAgent }),
  }, context.company_id)
}

export function isPeriodicalProject(project: CoworkProject | undefined) {
  return (
    project?.project_metadata?.work_organization === "periodical" || project?.project_metadata?.period_driven === true
  )
}

export function isAccountingProject(project: CoworkProject | undefined) {
  return (
    project?.project_metadata?.accounting_enabled === true || project?.project_metadata?.project_type === "accounting"
  )
}

export async function ensureRemoteSession(projectID: string, sessionID: string, companyID?: string) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions/${sessionID}/remote`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
  })
}

export async function syncRemoteSessionMessages(remoteSessionID: string) {
  return requestJson<{ synced: number; title: string; title_updated: boolean }>(
    `/api/sessions/remote/${remoteSessionID}/sync`,
    {
      method: "POST",
      headers: await jsonHeaders(),
    },
  )
}

export async function deleteCoworkSession(projectID: string, sessionID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/sessions/${sessionID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function renameCoworkSession(projectID: string, sessionID: string, title: string) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions/${sessionID}/title`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify({ title }),
  })
}

export async function createIntegrationConnectSession(providerKey: string) {
  return requestJson<CoworkConnectSession>(`/api/integrations/connect-session`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ allowed_integrations: [providerKey] }),
  })
}

export async function listIntegrationConnections(providerKey?: string) {
  const query = providerKey ? `?provider_key=${encodeURIComponent(providerKey)}` : ""
  return requestJson<{ connections: CoworkIntegrationConnection[] }>(`/api/integrations/connections${query}`).catch(
    () => ({ connections: [] }),
  )
}

export async function deleteIntegrationConnection(providerKey: string, connectionID: string) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/integrations/connections?provider_key=${encodeURIComponent(providerKey)}&connection_id=${encodeURIComponent(connectionID)}`,
    {
      method: "DELETE",
      headers: await authHeaders(),
    },
  )
  if (!response.ok) throw new Error(await response.text())
}

export async function listIntegrationFiles(input: {
  providerKey: string
  connectionID: string
  query?: string
  cursor?: string
  path?: string
  folderID?: string
}) {
  const params = new URLSearchParams({ provider_key: input.providerKey, connection_id: input.connectionID })
  if (input.query) params.set("query", input.query)
  if (input.cursor) params.set("cursor", input.cursor)
  if (input.path) params.set("path", input.path)
  if (input.folderID) params.set("folder_id", input.folderID)
  return requestJson<{ items: CoworkCloudFile[]; next_cursor?: string | null }>(
    `/api/integrations/files?${params.toString()}`,
  )
}

export async function importIntegrationFiles(input: {
  projectID: string
  workflowID?: string | null
  sessionID?: string | null
  providerKey: string
  connectionID: string
  files: CoworkCloudFile[]
}) {
  return requestJson<{
    imported_count: number
    skipped_count: number
    results: Array<{
      external_file_id: string
      status: string
      file_id?: string | null
      file_name?: string | null
      reason?: string | null
    }>
  }>(`/api/integrations/files/import`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({
      project_id: input.projectID,
      workflow_id: input.workflowID,
      session_id: input.sessionID,
      provider_key: input.providerKey,
      connection_id: input.connectionID,
      files: input.files.map((file) => ({
        external_file_id: file.id,
        name: file.name,
        mime_type: file.mime_type,
        size: file.size,
        path: file.path,
        version: file.version,
      })),
    }),
  })
}

export function buildIntegrationConnectUrl(session: CoworkConnectSession) {
  try {
    const source = new URL(session.connect_link)
    const sessionToken = source.searchParams.get("session_token")
    if (!sessionToken) return session.connect_link
    const connectUiBase = import.meta.env.VITE_NANGO_CONNECT_UI_BASE_URL || "https://connect.integrations.andmera.com"
    const target = new URL(connectUiBase)
    const apiURL = source.hostname.startsWith("connect.")
      ? import.meta.env.VITE_NANGO_API_URL || "https://integrations.andmera.com"
      : source.origin
    target.searchParams.set("session_token", sessionToken)
    target.searchParams.set("apiURL", apiURL)
    return target.toString()
  } catch {
    return session.connect_link
  }
}

export async function listMessages(projectID: string, sessionID: string) {
  return requestJson<CoworkMessage[]>(`/api/projects/${projectID}/sessions/${sessionID}/messages`)
}

export async function listFiles(projectID: string, workflowID?: string, companyID?: string) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkFile[]>(`/api/projects/${projectID}/files${query}`, undefined, companyID)
}

export async function listPortfolioFiles(options?: {
  entityID?: string
  projectID?: string
  periodID?: string
  companyID?: string
}) {
  const query = new URLSearchParams()
  query.set("entity_id", options?.entityID || "ALL")
  if (options?.projectID) query.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") query.set("period_id", options.periodID)
  return requestJson<CoworkPortfolioFile[]>(`/api/files?${query.toString()}`, undefined, options?.companyID)
}

export async function getPortfolioFileContentBlob(fileID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/files/${fileID}/content`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function listPortfolioFileCatalog(options: {
  entityID: string
  projectID?: string
  periodID?: string
  companyID?: string
  search?: string
  sellerIDs?: string[]
  documentTypes?: string[]
  category?: string
  fileIDs?: string[]
}) {
  const query = new URLSearchParams({ entity_id: options.entityID })
  if (options.projectID) query.set("project_id", options.projectID)
  if (options.periodID) query.set("period_id", options.periodID)
  if (options.search) query.set("q", options.search)
  if (options.category) query.set("category", options.category)
  for (const id of options.sellerIDs ?? []) query.append("seller_id", id)
  for (const type of options.documentTypes ?? []) query.append("document_type", type)
  for (const id of options.fileIDs ?? []) query.append("file_id", id)
  return requestJson<CoworkFileCatalog>(`/api/files/catalog?${query}`, undefined, options.companyID)
}

export async function listFileExtractions(
  fileID: string,
  options: {
    companyID: string
    periodID?: string | null
    runID?: string
    schemaName?: string
  },
) {
  const query = new URLSearchParams()
  if (options.periodID) query.set("period_id", options.periodID)
  if (options.runID) query.set("workflow_run_id", options.runID)
  if (options.schemaName) query.set("schema_name", options.schemaName)
  return requestJson<CoworkFileExtraction[]>(
    `/api/files/${encodeURIComponent(fileID)}/extractions?${query}`,
    undefined,
    options.companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function listProjectKnowledge(projectID: string, companyID: string) {
  return requestJson<CoworkKnowledge[]>(`/api/projects/${projectID}/knowledge`, undefined, companyID)
}

export async function listCompanyKnowledge(companyID: string) {
  return requestJson<CoworkKnowledge[]>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge`,
    undefined,
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function uploadCompanyKnowledge(companyID: string, file: File) {
  const form = new FormData()
  form.append("file", file)
  return requestJson<CoworkKnowledge>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge/upload`,
    { method: "POST", headers: await authHeaders(false, companyID), body: form },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function linkCompanyKnowledge(companyID: string, fileID: string) {
  return requestJson<CoworkKnowledge>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge/link`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ file_id: fileID }) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function removeCompanyKnowledge(companyID: string, knowledgeID: string) {
  return requestJson<void>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge/${encodeURIComponent(knowledgeID)}`,
    { method: "DELETE" },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function getCompanyKnowledgeContentBlob(companyID: string, knowledgeID: string) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/companies/${encodeURIComponent(companyID)}/knowledge/${encodeURIComponent(knowledgeID)}/content`,
    { headers: await authHeaders(false, companyID) },
  )
  if (!response.ok) await handleAuthFailure(response, undefined, true)
  return response.blob()
}

export async function uploadProjectKnowledge(projectID: string, file: File, companyID: string) {
  const form = new FormData()
  form.append("file", file)
  return requestJson<CoworkKnowledge>(`/api/projects/${projectID}/knowledge/upload`, {
    method: "POST",
    headers: await authHeaders(false, companyID),
    body: form,
  })
}

export async function linkProjectKnowledge(projectID: string, fileID: string, companyID: string) {
  return requestJson<CoworkKnowledge>(
    `/api/projects/${projectID}/knowledge/link`,
    {
      method: "POST",
      headers: await jsonHeaders(companyID),
      body: JSON.stringify({ file_id: fileID }),
    },
    companyID,
  )
}

export async function removeProjectKnowledge(projectID: string, knowledgeID: string, companyID: string) {
  return requestJson<void>(
    `/api/projects/${projectID}/knowledge/${knowledgeID}`,
    {
      method: "DELETE",
      headers: await authHeaders(false, companyID),
    },
    companyID,
  )
}

export async function getProjectKnowledgeContentBlob(projectID: string, knowledgeID: string, companyID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/knowledge/${knowledgeID}/content`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function getFile(projectID: string, fileID: string, companyID?: string) {
  return requestJson<CoworkFileDetail>(`/api/projects/${projectID}/files/${fileID}`, undefined, companyID)
}

export async function getFileContentBlob(projectID: string, fileID: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/files/${fileID}/content`, {
    headers: await authHeaders(false, company),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function deleteFile(projectID: string, fileID: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/files/${fileID}`, {
    method: "DELETE",
    headers: await authHeaders(false, company),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listProjectMembers(projectID: string) {
  return requestJson<CoworkProjectMember[]>(`/api/projects/${projectID}/members`)
}

export async function listArtifacts(projectID: string) {
  return requestJson<CoworkArtifact[]>(`/api/projects/${projectID}/artifacts`)
}

export async function getArtifact(projectID: string, artifactID: string, companyID?: string) {
  return requestJson<CoworkArtifact>(
    `/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}`,
    undefined,
    companyID,
  )
}

export async function listPortfolioReports(options?: {
  entityID?: string
  projectID?: string
  periodID?: string
  status?: string
}) {
  const query = new URLSearchParams()
  query.set("entity_id", options?.entityID || "ALL")
  if (options?.projectID) query.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") query.set("period_id", options.periodID)
  if (options?.status) query.set("status", options.status)
  return requestJson<CoworkPortfolioReport[]>(`/api/reports?${query.toString()}`)
}

export async function downloadPortfolioReport(artifactID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/reports/${artifactID}/download`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export class ReportPdfExportError extends Error {
  constructor(readonly status: number) {
    super("Report PDF export failed")
  }
}

export async function exportPortfolioReportPdf(artifactID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/reports/${encodeURIComponent(artifactID)}/export/pdf`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new ReportPdfExportError(response.status)
  if (!response.headers.get("Content-Type")?.includes("application/pdf")) throw new ReportPdfExportError(502)
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report.pdf",
  }
}

export async function listArtifactsForWorkflow(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkArtifact[]>(`/api/projects/${projectID}/artifacts${query}`)
}

export async function listArtifactsForSession(projectID: string, sessionID: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  return requestJson<CoworkArtifact[]>(
    `/api/projects/${projectID}/artifacts?session_id=${encodeURIComponent(sessionID)}`,
    undefined,
    company,
  ).catch(() => [])
}

export async function listExtractions(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkExtraction[]>(`/api/projects/${projectID}/extractions${query}`).catch(() => [])
}

export async function listTransformations(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkTransformation[]>(`/api/projects/${projectID}/transformations${query}`).catch(() => [])
}

export async function listApprovals(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkApproval[]>(`/api/projects/${projectID}/approvals${query}`).catch(() => [])
}

export async function listPortfolioApprovals(options?: {
  entityID?: string
  projectID?: string
  periodID?: string
  status?: string
}) {
  const query = new URLSearchParams()
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") query.set("period_id", options.periodID)
  if (options?.status) query.set("status", options.status)
  return requestJson<CoworkPortfolioApproval[]>(`/api/approvals?${query.toString()}`)
}

export async function requestApprovals(
  projectID: string,
  reportArtifactID: string,
  approverEmails: string[],
  message?: string,
  companyID?: string,
) {
  const company = companyID || activeCompanyID()
  return requestJson<CoworkApproval[]>(
    `/api/projects/${projectID}/approvals`,
    {
      method: "POST",
      headers: await jsonHeaders(company),
      body: JSON.stringify({ report_artifact_id: reportArtifactID, approver_emails: approverEmails, message }),
    },
    company,
    { preserveAuthOnForbidden: true },
  )
}

export async function updateApprovalReviewer(
  projectID: string,
  approvalID: string,
  approverEmail: string,
  companyID?: string,
) {
  const headers: Record<string, string> = await jsonHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  return requestJson<CoworkApproval>(`/api/projects/${projectID}/approvals/${approvalID}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ approver_email: approverEmail }),
  })
}

export async function deleteApprovalReviewer(projectID: string, approvalID: string, companyID?: string) {
  const headers = await authHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/approvals/${approvalID}`, {
    method: "DELETE",
    headers,
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listReportShares(projectID: string, artifactID: string, companyID: string) {
  return requestJson<CoworkReportShare[]>(
    `/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}/shares`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function shareReport(projectID: string, artifactID: string, emails: string[], message: string | undefined, companyID: string) {
  return requestJson<CoworkReportShare[]>(
    `/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}/shares`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ recipient_emails: emails, message }) },
    companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getSharedReport(token: string) {
  return requestJson<CoworkSharedReport>(`/api/shared-reports/${encodeURIComponent(token)}`, { headers: {} }, undefined, { preserveAuthOnForbidden: true })
}

export async function downloadSharedReport(token: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/shared-reports/${encodeURIComponent(token)}/download`)
  if (!response.ok) throw new Error("Shared report download failed")
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || "report" }
}

export async function getProjectApprovalDetail(projectID: string, approvalID: string, companyID: string) {
  return requestJson<CoworkApprovalDetail>(
    `/api/projects/${encodeURIComponent(projectID)}/approvals/${encodeURIComponent(approvalID)}`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function addProjectApprovalComment(projectID: string, approvalID: string, comments: string, companyID: string) {
  return requestJson<CoworkApprovalDetail>(
    `/api/projects/${encodeURIComponent(projectID)}/approvals/${encodeURIComponent(approvalID)}/comments`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ comments }) },
    companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getApprovalReview(approvalID: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}?include_content=false`, { headers: {} })
}

export async function downloadApprovalReviewReport(approvalID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/reviews/${approvalID}/download`)
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function approveReview(approvalID: string, comments?: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function rejectReview(approvalID: string, comments?: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function saveReviewComments(approvalID: string, comments?: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function downloadArtifact(projectID: string, artifactID: string, companyID?: string) {
  return downloadArtifactFile(`/api/projects/${projectID}/artifacts/${artifactID}/download`, companyID)
}

export async function updateArtifactContent(
  projectID: string,
  artifactID: string,
  blob: Blob,
  updatedAt: string,
  companyID?: string,
) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}/content`,
    {
      method: "PUT",
      headers: {
        ...(await authHeaders(false, companyID)),
        "Content-Type": blob.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "X-Artifact-Updated-At": updatedAt,
      },
      body: blob,
    },
  )
  if (!response.ok) await handleAuthFailure(response, undefined, true)
  return (await response.json()) as CoworkArtifact
}

export async function downloadArtifactFile(path: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const baseURL = coworkApiBaseUrl()
  const pageURL = artifactDownloadPageURL(typeof window === "undefined" ? "" : window.location.href, baseURL)
  const downloadPath = artifactDownloadPath(path, pageURL, baseURL)
  if (!downloadPath) throw new Error("Unsupported artifact download path")
  const response = await fetch(`${baseURL}${downloadPath}`, {
    headers: await authHeaders(false, company),
  })
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function deleteArtifact(projectID: string, artifactID: string, companyID?: string) {
  const headers = await authHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/artifacts/${artifactID}`, {
    method: "DELETE",
    headers,
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function addProjectMember(projectID: string, identifier: string, role: "viewer" | "editor") {
  const body = identifier.includes("@") ? { email: identifier, role } : { user_id: identifier, role }
  return requestJson<CoworkProjectMember>(`/api/projects/${projectID}/members`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export async function removeProjectMember(projectID: string, userID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/members/${userID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function uploadFile(
  projectID: string,
  file: File,
  periodID?: string | null,
  sessionID?: string,
  companyID?: string,
) {
  const form = new FormData()
  form.append("file", file)
  const query = new URLSearchParams()
  // Explicit null means project-level; omission preserves existing default-period behavior.
  if (periodID === null) query.set("project_level", "true")
  if (periodID) query.set("period_id", periodID)
  if (sessionID) query.set("session_id", sessionID)
  return requestJson<CoworkFile>(`/api/projects/${projectID}/files${query.size ? `?${query.toString()}` : ""}`, {
    method: "POST",
    headers: await authHeaders(false, companyID),
    body: form,
  })
}

export async function getSessionContextByRemote(remoteSessionID: string, companyID?: string) {
  return requestJson<CoworkSessionContext>(
    `/api/sessions/remote/${remoteSessionID}/context`,
    undefined,
    companyID,
  ).catch(() => undefined)
}

export async function streamMessage(
  projectID: string,
  sessionID: string,
  content: string,
  onEvent: (event: CoworkStreamEvent) => void,
  options?: { applicationLocale?: string; reportOutputLanguage?: string },
) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/projects/${projectID}/sessions/${sessionID}/messages/stream`,
    {
      method: "POST",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        content,
        application_locale: options?.applicationLocale,
        report_output_language: options?.reportOutputLanguage,
      }),
    },
  )
  if (!response.ok) throw new Error(await response.text())
  if (!response.body) return

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const read = async (buffer: string): Promise<void> => {
    const next = await reader.read()
    const chunk = buffer + (next.value ? decoder.decode(next.value, { stream: !next.done }) : "")
    const events = chunk.split("\n\n")
    const remainder = events.pop() ?? ""
    events.forEach((event) => parseSseEvent(event, onEvent))
    if (next.done) {
      parseSseEvent(remainder, onEvent)
      return
    }
    return read(remainder)
  }
  return read("")
}

async function boundedRead<T>(path: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const error = new DOMException("Request timed out", "TimeoutError")
      controller.abort(error)
      reject(error)
    }, 20_000)
  })
  try {
    return await Promise.race([requestJson<T>(path, { signal: controller.signal }, company), timeout])
  } finally {
    clearTimeout(timer)
  }
}

async function requestJson<T>(
  path: string,
  init?: RequestInit,
  companyID?: string,
  options?: { preserveAuthOnForbidden?: boolean },
) {
  const headers = init?.headers ?? (await authHeaders(false, companyID))
  init?.signal?.throwIfAborted()
  const response = await fetch(`${coworkApiBaseUrl()}${path}`, {
    ...init,
    headers,
  })
  init?.signal?.throwIfAborted()
  if (response.status === 401 && !init?.headers) {
    const headers = await authHeaders(true, companyID)
    init?.signal?.throwIfAborted()
    const retry = await fetch(`${coworkApiBaseUrl()}${path}`, {
      ...init,
      headers,
    })
    init?.signal?.throwIfAborted()
    if (!retry.ok) await handleAuthFailure(retry, init?.signal, options?.preserveAuthOnForbidden)
    if (retry.status === 204) return undefined as T
    return (await retry.json()) as T
  }
  if (!response.ok) await handleAuthFailure(response, init?.signal, options?.preserveAuthOnForbidden)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function handleAuthFailure(
  response: Response,
  signal?: AbortSignal | null,
  _preserveAuthOnForbidden = false,
): Promise<never> {
  const text = await response.text()
  signal?.throwIfAborted()
  // Project, company, and agent access can be denied without invalidating the sign-in.
  if (response.status === 401) {
    try {
      sessionStorage.removeItem("cowork.activeCompanyId")
    } catch {
      // Continue clearing the persisted session if tab storage is unavailable.
    }
    try {
      localStorage.removeItem("cowork.auth.token")
      localStorage.removeItem("cowork.auth.email")
      localStorage.removeItem("cowork.auth.name")
      localStorage.removeItem("activeCompanyId")
      localStorage.removeItem("cowork.agent.companyId")
      localStorage.removeItem("cowork.agent.userId")
      localStorage.removeItem("cowork.agent.userEmail")
    } catch {
      // Ignore storage failures and surface the original API error below.
    }
    window.location.assign("/")
  }
  let message = text || "Request failed"
  try {
    const payload = JSON.parse(text) as { detail?: unknown }
    if (typeof payload.detail === "string" && payload.detail.trim()) message = payload.detail
  } catch {
    // Keep the plain response text when the API did not return JSON.
  }
  throw new CoworkApiError(response.status, message)
}

function parseSseEvent(raw: string, onEvent: (event: CoworkStreamEvent) => void) {
  const data = raw
    .split("\n")
    .filter((line) => line.startsWith("data: "))
    .map((line) => line.slice(6))
    .join("\n")
  if (!data || data === "[DONE]") return
  onEvent(JSON.parse(data) as CoworkStreamEvent)
}

function downloadFilename(contentDisposition: string | null) {
  if (!contentDisposition) return ""
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8?.[1]) return decodeURIComponent(utf8[1])
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return match?.[1] ?? ""
}

async function authHeaders(forceRefresh = false, companyID?: string): Promise<Record<string, string>> {
  if (localDevAuthEnabled()) {
    return { ...localDevHeaders(), "X-Active-Company-Id": companyID || activeCompanyID() }
  }
  const token = tokenProvider ? await tokenProvider(forceRefresh) : ""
  if (!token) return {}
  return {
    Authorization: `Bearer ${token}`,
    "X-Active-Company-Id": companyID || activeCompanyID(),
  }
}

function localDevAuthEnabled() {
  return import.meta.env.VITE_LOCAL_DEV_AUTH === "true"
}
import { activeCompanyID, localDevHeaders } from "@/cowork/local-auth"
